import { useEffect, useState } from 'react';

import { normalizeAlerts } from './helpers';

// setTimeout overflows (and fires immediately) past a signed 32-bit delay.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

const nowSeconds = () => Math.floor(Date.now() / 1000);

/**
 * Ms until the next epoch second at which an alert becomes eligible
 * (`startDate`) or stops being (just past `endDate`), if any.
 */
const delayToNextBoundaryMs = (boundariesKey: string, now: number) => {
  const next = boundariesKey
    .split(',')
    .map(Number)
    .filter(boundary => boundary > now);
  if (next.length === 0) return undefined;
  return Math.min(
    Math.max(Math.min(...next) * 1000 - Date.now(), 0),
    MAX_TIMEOUT_MS,
  );
};

/**
 * Current epoch seconds for `computeAlertsToShow`, held in state and advanced
 * only when an alert in `flag` reaches its start or end date, so an open app
 * re-evaluates the date window without polling.
 */
export const useAlertClock = (flag: unknown): number => {
  // Compared by value: flag objects may be re-created on every render.
  const boundariesKey = normalizeAlerts(flag)
    // Whole seconds, since `now` is floored: a fractional boundary would
    // otherwise fire early and reschedule at zero delay until it passes.
    .flatMap(({ startDate, endDate }) => [
      Math.ceil(startDate),
      Math.floor(endDate) + 1,
    ])
    .join(',');

  // Keyed so that a changed flag re-syncs `now`, which otherwise only moves at
  // boundaries and may be stale.
  const [clock, setClock] = useState(() => ({
    boundariesKey,
    now: nowSeconds(),
  }));

  useEffect(() => {
    const delayMs =
      clock.boundariesKey === boundariesKey
        ? delayToNextBoundaryMs(boundariesKey, clock.now)
        : // Catch up on the next tick.
          0;
    if (delayMs === undefined) return undefined;

    const handle = setTimeout(() => {
      setClock({ boundariesKey, now: nowSeconds() });
    }, delayMs);
    return () => {
      clearTimeout(handle);
    };
  }, [boundariesKey, clock]);

  return clock.now;
};
